/**
 * Valish coin's arithmetic and notation (ADR-124 decisions 1 and 5). Every stored, sent, computed or ledgered
 * amount is one whole number of copper; gold and silver exist only when an amount is shown or typed. Players
 * read the coins as the Sovereign (gold), the Talon (silver) and the Bit (copper); the code names the metals,
 * so "bits" never means binary digits. The short form `3s 45t 12b` is notation, not words: the server's logs,
 * the tools and the page print it from here, and `parseCoins` reads back that form and no other. Every step
 * is integer arithmetic on safe integers within `CURRENCY_MAX`, so no amount is ever rounded.
 */

/**
 * Copper to one silver, the Talon.
 */
export const COPPER_PER_SILVER = 100;

/**
 * Copper to one gold, the Sovereign.
 */
export const COPPER_PER_GOLD = 10_000;

/**
 * The most copper a character holds of a currency: exactly 1,000,000,000 Sovereigns, that amount itself allowed
 * (ADR-124's note for P2.8-T70, amending decision 1.4). Twice it is about 450 times under `Number.MAX_SAFE_INTEGER`,
 * so any sum of two balances is a safe integer. The check `character_currencies_amount` (migration 036) holds the same
 * number and changes with it.
 */
export const CURRENCY_MAX = 10_000_000_000_000;

/**
 * The currency code of Valish coin in the database, the ledger and the wire (ADR-124 decision 1.3.1).
 */
export const CURRENCY_COIN = "coin";

/**
 * Every currency the game holds, in the order a wallet lists them (ADR-124 decisions 2.4 and 4.1): Valish coin
 * alone until the rows that store Honor, Marks and Tokens add theirs. A stored code outside it is kept with the
 * character and never sent.
 */
export const CURRENCY_CODES: readonly string[] = Object.freeze([CURRENCY_COIN]);

/**
 * The most currencies one `wallet` carries (ADR-124 decision 4.1).
 */
export const MAX_WALLET_CURRENCIES = 8;

/**
 * The shape of every currency code (ADR-124 decision 2.1): 1 to 16 lowercase ASCII letters, as the check on
 * `character_currencies.currency` holds it, so `coin`, `honor`, `marks` and `tokens` all fit with no migration.
 */
export const CURRENCY_CODE_PATTERN = /^[a-z]{1,16}$/;

/**
 * One of the three coins, named by its metal: gold the Sovereign, silver the Talon, copper the Bit.
 */
export type Coin = "gold" | "silver" | "copper";

/**
 * Each coin's letter in the short form (the author's answer 2 of 2026-10-09, "3s 45t 12b"): the coin names'
 * initials, not the metals'.
 */
export const COIN_LETTERS: { readonly [coin in Coin]: string } = { gold: "s", silver: "t", copper: "b" };

/**
 * Each coin's art in the icon table (ADR-111's note for P2.18-T16, ADR-124 decision 5.3), rows of kind `item`; the
 * page draws a part of the coin form with it and `tests/icon-keys.test.ts` holds every key to a row.
 */
export const COIN_ICON_KEYS: { readonly [coin in Coin]: string } = Object.freeze({ gold: "coin-gold", silver: "coin-silver", copper: "coin-copper" });

/**
 * The currencies the wallet line draws after Valish coin as labelled stand-ins, in Bible 10.1's order: Honor, Marks of
 * the Lyceum and Inscription Tokens (ADR-124's note for P2.8-T59). No table row, ledger kind or `wallet` entry stores
 * them yet, so none is in `CURRENCY_CODES`; the row that first stores one moves its code there.
 */
export const STANDIN_CURRENCIES = Object.freeze(["honor", "marks", "tokens"] as const);

/**
 * A currency the wallet line draws as a stand-in.
 */
export type StandinCurrency = (typeof STANDIN_CURRENCIES)[number];

/**
 * Each stand-in currency's art in the icon table (ADR-111's note for P2.18-T16), rows of kind `item`, keyed by its
 * currency code; `tests/icon-keys.test.ts` holds every key to a row.
 */
export const CURRENCY_ICON_KEYS: { readonly [code in StandinCurrency]: string } = Object.freeze({ honor: "token-trident-hex", marks: "seal-red-wax", tokens: "rune-stone-grey" });

/**
 * One part of an amount: a coin and how many of it, a whole number.
 */
export type CoinPart = { readonly coin: Coin; readonly count: number };

/**
 * What `parseCoins` makes of a text: the copper it reads as, or refused with no guess.
 */
export type CoinsVerdict = { readonly kind: "ok"; readonly copper: number } | { readonly kind: "refused" };

/**
 * The one refusal `parseCoins` answers, shared so a refusal allocates nothing.
 */
const REFUSED: CoinsVerdict = { kind: "refused" };

/**
 * The most digits a Sovereign part takes: `CURRENCY_MAX` in Sovereigns, 1,000,000,000, has ten, so a longer part is
 * past the cap before it is converted.
 */
const MAX_GOLD_DIGITS = 10;

/**
 * A Sovereign part's number: digits with no leading zero past a single `0`, or the `en-GB` grouping, a first
 * group of one to three digits then groups of exactly three after commas.
 */
const GOLD_NUMBER = /^(?:0|[1-9][0-9]*|[1-9][0-9]{0,2}(?:,[0-9]{3})+)$/;

/**
 * A Talon or Bit part's number: 0 to 99 with no leading zero.
 */
const SMALL_NUMBER = /^(?:0|[1-9][0-9]?)$/;

/**
 * Whether a number is an amount of a currency a character may hold: a whole number from 0 to `CURRENCY_MAX`.
 * Negative zero counts as 0.
 *
 * @param n The number to judge.
 * @returns True for a whole number in 0 to `CURRENCY_MAX`.
 */
export function isCurrencyAmount(n: number): boolean {
  return Number.isSafeInteger(n) && n >= 0 && n <= CURRENCY_MAX;
}

/**
 * The quotient of a whole division of a safe integer, exact for every amount within the cap.
 *
 * @param value A non-negative safe integer.
 * @param divisor A positive safe integer.
 * @returns The number of whole divisors in the value.
 */
function wholeDivide(value: number, divisor: number): number {
  return (value - (value % divisor)) / divisor;
}

/**
 * An amount's parts, largest coin first, with the parts that are zero left out and nothing as one copper part
 * of 0 (ADR-124 decisions 5.1 and 5.2). 34,512 copper is 3 gold, 45 silver and 12 copper; 30,012 is 3 gold
 * and 12 copper.
 *
 * @param copper The amount in copper.
 * @returns The non-zero parts largest first, or one copper part of 0.
 * @throws {RangeError} When the amount is not a whole number in 0 to `CURRENCY_MAX`.
 */
export function coinParts(copper: number): readonly CoinPart[] {
  if (!isCurrencyAmount(copper)) {
    throw new RangeError(`not a currency amount: ${copper}`);
  }
  const gold = wholeDivide(copper, COPPER_PER_GOLD);
  const silver = wholeDivide(copper % COPPER_PER_GOLD, COPPER_PER_SILVER);
  const rest = copper % COPPER_PER_SILVER;
  const parts: CoinPart[] = [];
  if (gold > 0) {
    parts.push({ coin: "gold", count: gold });
  }
  if (silver > 0) {
    parts.push({ coin: "silver", count: silver });
  }
  if (rest > 0 || parts.length === 0) {
    parts.push({ coin: "copper", count: rest });
  }
  return parts;
}

/**
 * An amount's three parts, largest coin first, the zeros kept (ADR-124's note for P2.8-T59): the wallet line draws
 * every coin whatever the amount. 1,200 copper is 0 gold, 12 silver and 0 copper.
 *
 * @param copper The amount in copper.
 * @returns The gold, silver and copper parts, in that order.
 * @throws {RangeError} When the amount is not a whole number in 0 to `CURRENCY_MAX`.
 * @example
 * allCoinParts(1200);
 * // [{ coin: "gold", count: 0 }, { coin: "silver", count: 12 }, { coin: "copper", count: 0 }]
 */
export function allCoinParts(copper: number): readonly [CoinPart, CoinPart, CoinPart] {
  if (!isCurrencyAmount(copper)) {
    throw new RangeError(`not a currency amount: ${copper}`);
  }
  return [
    { coin: "gold", count: wholeDivide(copper, COPPER_PER_GOLD) },
    { coin: "silver", count: wholeDivide(copper % COPPER_PER_GOLD, COPPER_PER_SILVER) },
    { coin: "copper", count: copper % COPPER_PER_SILVER },
  ];
}

/**
 * A whole number written with the `en-GB` thousands separator, built by hand so every runtime prints it alike.
 *
 * @param n A non-negative safe integer.
 * @returns The digits in groups of three from the right, separated by commas.
 */
function grouped(n: number): string {
  const digits = String(n);
  const head = digits.length % 3 || 3;
  let text = digits.slice(0, head);
  for (let at = head; at < digits.length; at += 3) {
    text += `,${digits.slice(at, at + 3)}`;
  }
  return text;
}

/**
 * A part's number as every form writes it (ADR-124 decisions 5.3 and 5.4): the Sovereign part grouped with the `en-GB`
 * separator, a Talon or Bit part plain, since it never passes 99.
 *
 * @param part The part.
 * @returns Its number.
 * @example
 * coinCount({ coin: "gold", count: 1234 });
 * // "1,234"
 */
export function coinCount(part: CoinPart): string {
  return part.coin === "gold" ? grouped(part.count) : String(part.count);
}

/**
 * An amount in the short form, where only text goes (ADR-124 decision 5.4): each part as its number and its
 * coin's letter, separated by a space, the Sovereign part grouped with the `en-GB` separator. `3s 45t 12b`,
 * `1,234s`, and `0b` for nothing.
 *
 * @param copper The amount in copper.
 * @returns The short form.
 * @throws {RangeError} When the amount is not a whole number in 0 to `CURRENCY_MAX`.
 */
export function shortCoins(copper: number): string {
  return coinParts(copper)
    .map((part) => `${coinCount(part)}${COIN_LETTERS[part.coin]}`)
    .join(" ");
}

/**
 * A change of an amount in the short form with its sign before the first part (ADR-124 decision 5.6): `+45t`,
 * `-3s 12b`. No change reads `0b` with no sign.
 *
 * @param delta The change in copper, negative for a loss.
 * @returns The signed short form.
 * @throws {RangeError} When the change is not a whole number or is larger than `CURRENCY_MAX` either way.
 */
export function signedShortCoins(delta: number): string {
  if (!Number.isSafeInteger(delta) || Math.abs(delta) > CURRENCY_MAX) {
    throw new RangeError(`not a currency change: ${delta}`);
  }
  if (delta === 0) {
    return shortCoins(0);
  }
  return `${delta < 0 ? "-" : "+"}${shortCoins(Math.abs(delta))}`;
}

/**
 * Reads a typed amount in the short form (ADR-124 decision 5.7) and never throws. Parts are split by one or
 * more spaces (U+0020 only) and spaces at either end are ignored; each part is digits then its letter with
 * nothing between, the letters lower case, in the order s, t, b, each at most once. The Sovereign part may
 * carry the `en-GB` grouping; Talons and Bits are 0 to 99; no number has a leading zero past a single `0`. A
 * part of zero adds nothing, so `0s 5b` is 5. Anything else, or a total past `CURRENCY_MAX`, is refused.
 *
 * @example
 * parseCoins("3s 45t 12b"); // { kind: "ok", copper: 34512 }
 * parseCoins("1,234s"); // { kind: "ok", copper: 12340000 }
 * parseCoins("3s45t"); // { kind: "refused" }
 *
 * @param text The typed amount.
 * @returns The copper it reads as, or refused.
 */
export function parseCoins(text: string): CoinsVerdict {
  const parts = text.split(" ").filter((part) => part.length > 0);
  if (parts.length === 0 || parts.length > 3) {
    return REFUSED;
  }
  let copper = 0;
  let next = 0;
  for (const part of parts) {
    const letter = part.slice(-1);
    const number = part.slice(0, -1);
    const order = letter === COIN_LETTERS.gold ? 0 : letter === COIN_LETTERS.silver ? 1 : letter === COIN_LETTERS.copper ? 2 : -1;
    if (order < next) {
      return REFUSED;
    }
    next = order + 1;
    if (order === 0) {
      if (!GOLD_NUMBER.test(number)) {
        return REFUSED;
      }
      const digits = number.replaceAll(",", "");
      if (digits.length > MAX_GOLD_DIGITS) {
        return REFUSED;
      }
      copper += Number(digits) * COPPER_PER_GOLD;
    } else {
      if (!SMALL_NUMBER.test(number)) {
        return REFUSED;
      }
      copper += Number(number) * (order === 1 ? COPPER_PER_SILVER : 1);
    }
  }
  return copper <= CURRENCY_MAX ? { kind: "ok", copper } : REFUSED;
}
